'use client'

import { ReactNode, useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import i18n from '@/config/i18n'
import { useLang } from '@/store/useLanguageStore'
import { changeLanguageAndPersist, isSupportedLang, Lang, setLangCookie } from '@/utils/localization/language'

export default function I18nProvider({ children }: { children: ReactNode }) {
  const { setLang } = useLang()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const localeParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('locale') : null
    if (isSupportedLang(localeParam)) {
      const lang = localeParam as Lang
      changeLanguageAndPersist(lang)
      setLang(lang)
      setMounted(true)
      return
    }
    const getLangFromCookie = (): string | null => {
      const match = document.cookie.match(/lang=([^;]+)/)
      return match ? match[1] : null
    }

    const storedLang = getLangFromCookie()

    const setLanguageFromBrowserLocale = async () => {
      const browserLang = navigator.language // Get browser language
      let lang: Lang = 'ko' // Default to Korean

      if (browserLang.startsWith('zh-Hans')) {
        lang = 'zh-CN' // Simplified Chinese
      } else if (browserLang.startsWith('zh-Hant')) {
        lang = 'zh-TW' // Traditional Chinese
      } else if (browserLang.startsWith('jp')) {
        lang = 'jp' // Japanese
      } else if (browserLang.startsWith('en')) {
        lang = 'en' // Set to English only if the browser language is English
      }

      setLangCookie(lang)
      setLang(lang)
    }

    if (isSupportedLang(storedLang)) {
      setLang(storedLang as Lang)
      setMounted(true)
    } else {
      setLanguageFromBrowserLocale().then(() => setMounted(true))
    }
  }, [setLang])

  if (!mounted) {
    return null
  }

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}
